import { getState } from "./LocalStorage";
import { amplitudeTracker } from "./amplitudeTracker";

export const px2vw = (size, width = 1440) => `${(size / width) * 100}vw`;

export const convertDate = (inputFormat) => {
  function pad(s) {
    return s < 10 ? "0" + s : s;
  }
  const d = new Date(inputFormat);
  return [pad(d.getDate()), pad(d.getMonth() + 1), d.getFullYear()].join("-");
};

export const consumerWebsite = (subDomain, redirection) => {
  // In dev, use local KB website URL so preview works without hitting production domain
  const consumerPreviewUrl = process.env.REACT_APP_CONSUMER_PREVIEW_URL;
  if (consumerPreviewUrl) {
    const base = consumerPreviewUrl.replace(/\/$/, "");
    return redirection ? `${base}/${redirection}` : `${base}/`;
  }

  let domainName = "";
  let url = "";
  const customDomainDetails = getState("customDomainDetails");
  if (customDomainDetails) {
    domainName = customDomainDetails?.customDomain;
    url = `https://${domainName}/`;
  } else {
    domainName = process.env.REACT_APP_DOMAIN_NAME;
    url = `https://${subDomain}${domainName}/`;
  }
  if (redirection) {
    return `${url}${redirection}`;
  } else {
    return `${url}`;
  }
};

export function copyToClipboard(textToCopy) {
  // navigator clipboard api needs a secure context (https)
  if (navigator.clipboard && window.isSecureContext) {
    // navigator clipboard api method'
    return navigator.clipboard.writeText(textToCopy);
  } else {
    // text area method
    let textArea = document.createElement("textarea");
    textArea.value = textToCopy;
    // make the textarea out of viewport
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    return new Promise((res, rej) => {
      // here the magic happens
      document.execCommand("copy") ? res() : rej();
      textArea.remove();
    });
  }
}

export const debounce = (func, wait, immediate) => {
  let timeout;
  return function executedFunction() {
    let context = this;
    let args = arguments;
    let later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    let callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
};

export const capitalizeFirstLetter = (string) =>
  string?.charAt(0).toUpperCase() + string?.slice(1).toLowerCase();

export const getImageName = (name) =>
  name?.split("_")[name?.split("_").length - 1];

const downloadFile = ({ data, fileName, fileType }) => {
  const blob = new Blob([data], { type: fileType });

  const a = document.createElement("a");
  a.download = fileName;
  a.href = window.URL.createObjectURL(blob);
  const clickEvt = new MouseEvent("click", {
    view: window,
    bubbles: true,
    cancelable: true,
  });
  a.dispatchEvent(clickEvt);
  a.remove();
};

function stripHtml(html) {
  let tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

export const exportToCsv = (usersData) => {
  // RFC 4180: wrap every field in double quotes and escape internal quotes by doubling them
  const escapeCsvField = (value) => {
    const str = value == null ? "" : String(value);
    return `"${str.replace(/"/g, '""')}"`;
  };

  const columnKeys = Object.keys(usersData[0]);
  const headerRow = columnKeys
    .map((key) => escapeCsvField(key.charAt(0).toUpperCase() + key.slice(1)))
    .join(",");

  const dataRows = usersData.map((user) => {
    const {
      articleId,
      title,
      content,
      status,
      author,
      categoryId,
      categoryTitle,
      createdAt,
      modifiedAt,
    } = user;

    const cleanContent =
      content && content.length > 0 ? stripHtml(content) : "";

    return [
      articleId,
      title,
      cleanContent,
      status,
      author,
      categoryId,
      categoryTitle,
      createdAt,
      modifiedAt,
    ]
      .map(escapeCsvField)
      .join(",");
  });

  // UTF-8 BOM ensures Excel and Google Sheets interpret encoding correctly
  const BOM = "\uFEFF";
  downloadFile({
    data: BOM + [headerRow, ...dataRows].join("\n"),
    fileName: "hiverdocs_export_" + new Date().toISOString().split("T")[0] + ".csv",
    fileType: "text/csv;charset=utf-8",
  });
};

export const trimText = (text, maxLength) => {
  const value = text?.trim();
  if (value?.length > maxLength) {
    return value.substring(0, maxLength) + "...";
  }
  return value;
};

export const timeSince = (date) => {
  if (typeof date !== "object") {
    date = new Date(date);
  }

  var seconds = Math.floor((new Date() - date) / 1000);
  var intervalType;

  var interval = Math.floor(seconds / 31536000);
  if (interval >= 1) {
    intervalType = "year";
  } else {
    interval = Math.floor(seconds / 2592000);
    if (interval >= 1) {
      intervalType = "month";
    } else {
      interval = Math.floor(seconds / 86400);
      if (interval >= 1) {
        intervalType = "day";
      } else {
        interval = Math.floor(seconds / 3600);
        if (interval >= 1) {
          intervalType = "hour";
        } else {
          interval = Math.floor(seconds / 60);
          if (interval >= 1) {
            intervalType = "minute";
          } else {
            intervalType = "minute";
            interval = "about 1";
          }
        }
      }
    }
  }

  if (interval > 1 || interval === 0) {
    intervalType += "s";
  }

  return interval + " " + intervalType;
};

export async function checkImage(url) {
  return fetch(url)
    .then((res) => res)
    .catch(() => false);
}

export const handleGainsight = (siteSettingsData, eventName, data = {}) => {
  const users = siteSettingsData?.users?.[0],
    userid = users?.userId;
  if (userid) {
    data = { ...data, userid: userid };
    if (users?.usergroupId) {
      data.usergroupid = users.usergroupId;
    }
    sendGainsightEvent(eventName, data);
  }
}

export const identifyGainsightUser = (userId, usergroupId) => {
  return;
};

export const sendGainsightEvent = (event, data = {}) => {
  amplitudeTracker.track(event, data);
}